import { Button, Icons, useToggle } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useNotification } from '../../../hooks/useNotification';
import { ConfirmationModal } from '../../../molecules/modals/ConfirmationModal';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';
import { ServiceRequestTaskLabel } from '../labels';
import { ServiceRequestTaskIconConfig } from '../taskIconConfig';
import { getCompletedTaskLabel } from './getCompletedTaskLabel';

const MarkAsCompleteTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { setNotification } = useNotification();
  const { role, mutate } = context;

  const markAsCompleteModalVisibility = useToggle();
  const isMarkingAsComplete = useToggle(false);
  const completedLabel = getCompletedTaskLabel({
    task,
    serviceRequest: context.serviceRequest,
  });

  const markFilingAsComplete = async () => {
    isMarkingAsComplete.on();
    const result = await updateTaskStatus({
      task,
      mutate,
      status: TaskStatus.COMPLETED,
    });

    if (!result) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to mark as complete',
      });
      markAsCompleteModalVisibility.hide();
      isMarkingAsComplete.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Task marked as complete',
    });
    markAsCompleteModalVisibility.hide();
    isMarkingAsComplete.off();
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions:
              task.pendingOn === TaskParty.CLIENT ? (
                <Button size="sm" onClick={markAsCompleteModalVisibility.show}>
                  <Icons.Check className="w-4" />
                  Mark as completed
                </Button>
              ) : null,
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" variant="secondary" disabled>
                Mark as completed
              </Button>
            ),
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={markAsCompleteModalVisibility.show}
                variant={
                  task.pendingOn === TaskParty.CLIENT ? 'secondary' : 'primary'
                }
              >
                <Icons.Check className="w-4" />
                {task.pendingOn === TaskParty.CLIENT
                  ? "Mark as completed on client's behalf"
                  : 'Mark as completed'}
              </Button>
            ),
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
        [TaskParty.ADMIN]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button size="sm" onClick={markAsCompleteModalVisibility.show}>
                <Icons.Check className="w-4" />
                {task.pendingOn === TaskParty.CLIENT
                  ? "Mark as completed on client's behalf"
                  : 'Mark as completed'}
              </Button>
            ),
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={markAsCompleteModalVisibility.show}
              >
                <Icons.Check className="w-4" />
                Continue and mark as completed
              </Button>
            ),
            label: <ServiceRequestTaskLabel label={task.title} />,
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={completedLabel}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={markAsCompleteModalVisibility.visible}
        onClose={markAsCompleteModalVisibility.hide}
        onConfirm={markFilingAsComplete}
        title="Mark as done"
        message="Please note that this action will update the customer. To revert this change, please contact Commenda support."
        confirmButtonText="Continue"
        hideCancelButton
        isActionLoading={isMarkingAsComplete.value}
      />
    </div>
  );
};

export default MarkAsCompleteTask;
